import { Button, ButtonProps, Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { CSSProperties } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { selectDeadline, selectOrder } from "../../store/timer/timer.selector";
import { contactUs, getTimeSecondsFromNow } from "../../utils/utils";
import { selectOrgsCount } from "../../store/org/org.selector";
import { newTeamName } from "../../utils/constants";
import { selectIsOnlySummaryTrialPack } from "../../store/subscription/subscription.selector";
import {
  useCancelOrderMutation,
  useCreateOrgMutation,
  useDeleteTeamsOnCancelMutation,
  useUsersSyncQuery,
} from "../../store/api/api";
import {
  setDeadline,
  setModalOpen,
  setOrder,
  setReferredOrgId,
  setReferredSFNumber,
} from "../../store/timer/timer.reducer";

export const paymentDuration = Number(process.env.REACT_APP_PAYMENT_DURATION);

interface PaymentButtonProps extends ButtonProps {
  paymentText: "Buy a new Number !" | "Renew" | "Pay Now";
  SFNumber?: string;
  orgId?: number;
  orgName?: string;
  textStyles?: CSSProperties;
  collapsed?: boolean;
}

export const PaymentButton = (props: Readonly<PaymentButtonProps>) => {
  const {
    paymentText,
    SFNumber,
    orgId,
    orgName = "",
    textStyles,
    collapsed = false,
    ...rest
  } = props;
  const { data: usersSync } = useUsersSyncQuery();
  const dispatch = useDispatch();
  const deadline = useSelector(selectDeadline);
  const orgsCount = useSelector(selectOrgsCount);
  const [triggerDeleteTeams] = useDeleteTeamsOnCancelMutation();
  const order = useSelector(selectOrder);
  const openSuperfone = () => {
    dispatch(setReferredSFNumber(SFNumber || ""));
    dispatch(setReferredOrgId(orgId || 0));
    dispatch(setModalOpen(true));
  };
  const [createOrgTrigger, { isLoading }] = useCreateOrgMutation();

  const startPayment = () => {
    cancelPreviousCleanup();
    const deadline = getTimeSecondsFromNow(paymentDuration);
    dispatch(setDeadline(deadline));
    openSuperfone();
  };
  const [triggerCancelOrder] = useCancelOrderMutation();
  const cancelPreviousCleanup = () => {
    if (order?.id) {
      triggerCancelOrder(order.id);
      dispatch(setOrder(null));
    }
    dispatch(setReferredSFNumber(""));
    dispatch(setReferredOrgId(0));
    triggerDeleteTeams(orgId);
  };

  const goToSuperfonePayments = () => {
    if (paymentText === "Renew" && !SFNumber) {
      Modal.info({
        title: (
          <div>
            <span>{`Superfone Number for Team`}</span>
            <strong>&nbsp;{orgName || newTeamName}&nbsp;</strong>
            <span>{`is disabled.`}</span>
          </div>
        ),
        content: (
          <div>
            <p>{`Please contact Superfone.`}</p>
          </div>
        ),
        onOk() {},
      });
      return;
    }
    if (!deadline) {
      if (paymentText === "Buy a new Number !") {
        createOrgTrigger({ name: newTeamName });
        return;
      } else {
        startPayment();
      }
    } else {
      Modal.confirm({
        title: "You already have a payment in progress",
        icon: <ExclamationCircleOutlined />,
        content: "Cancel and start a new Payment?",
        cancelText: "Continue older payment",
        okText: "Start new",
        onOk() {
          if (paymentText === "Buy a new Number !") {
            cancelPreviousCleanup();
            createOrgTrigger({ name: newTeamName });
            return;
          } else {
            startPayment();
          }
        },
        cancelButtonProps: {
          onClick: () => {
            dispatch(setModalOpen(true));
            Modal.destroyAll();
          },
        },
        closable: true,
      });
    }
  };
  const maxOrgCount = usersSync?.user?.org_creation_limit || 0;
  const goToWhatsapp = () => {
    contactUs("I want to purchase additional superfone number");
  };

  const onClick: React.MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (
      paymentText === "Buy a new Number !" ? orgsCount === maxOrgCount : false
    ) {
      goToWhatsapp();
    } else {
      goToSuperfonePayments();
    }
  };
  const isOnlySummaryTrialPack = useSelector(selectIsOnlySummaryTrialPack);
  const isDisabled = () => {
    if (paymentText === "Buy a new Number !" && isOnlySummaryTrialPack) {
      return true;
    }
    return rest?.hasOwnProperty("disabled") ? rest.disabled : isLoading;
  };
  return (
    <Button
      {...rest}
      type={rest?.type || "primary"}
      onClick={onClick}
      disabled={isDisabled()}
      loading={paymentText === "Buy a new Number !" && isLoading}
    >
      <span style={textStyles} className={collapsed ? "!hidden" : ""}>
        {paymentText === "Buy a new Number !" && orgsCount === maxOrgCount
          ? "Contact Us to Buy a New Number"
          : paymentText}
      </span>
    </Button>
  );
};
